import { useEffect, useId, useRef, useState, type ReactElement } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  clientsService,
  type MccCheckConfig,
  type MccCheckResult,
} from "@/services/clientsService";

/**
 * Read-only "is this Google Ads account in our MCC?" check (Task 2).
 *
 * ⛔ NOTHING IS SAVED FROM HERE. No save button, no link to the connections
 * editor: saving a Google Ads customer id on a client that has a Planner
 * contract turns on live budget pacing, and the Ads team links last. The card
 * only asks the backend one read and shows the answer.
 *
 * variant "panel": heading + card chrome (client Integrations tab).
 * variant "embedded": the same input / Check / results / footer, no heading.
 * Renders nothing while the config loads and when the check is switched off on
 * this environment (the config GET answers 503).
 */
export type AdsMccCheckVariant = "panel" | "embedded";

const INVALID_MESSAGE = "Enter the 10-digit customer ID (dashes are fine).";
const UNAVAILABLE_MESSAGE = "Couldn't check right now";
const CUSTOMER_ID_PATTERN = /^\d{3}-?\d{3}-?\d{4}$/;

type ConfigState =
  | { status: "loading" }
  | { status: "off" }
  | { status: "error" }
  | { status: "ready"; config: MccCheckConfig };

function responseStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status;
}

function mccLabel(result: Pick<MccCheckConfig, "mcc_id_formatted" | "env">): string {
  return `${result.mcc_id_formatted} (${result.env})`;
}

function formatCustomerId(digits: string): string {
  return digits.length === 10
    ? `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
    : digits;
}

// Copy pending Art's check (Task 2 Q3); keep GOOGLE_ADS_MCC_CHECK_ENABLED off until then.
function LinkSteps({ mccFormatted }: { mccFormatted: string | null }) {
  return (
    <div data-testid="mcc-link-steps" className="space-y-2 text-sm">
      <p>
        To link it, the client (or whoever has admin access to their Google Ads account)
        does this:
      </p>
      <ol className="list-decimal space-y-1 pl-5">
        <li>Sign in to their Google Ads account (ads.google.com).</li>
        <li>Go to Admin, then Access and security, then Managers.</li>
        <li>
          {`Click the plus button, enter our MCC ID ${mccFormatted ?? ""}, and send the request.`}
        </li>
        <li>Someone on our team accepts the request in our MCC.</li>
      </ol>
      <p className="text-muted-foreground">
        Once it is accepted, check again here. Linking does not turn on budget pacing. That
        only happens when the Ads team saves the customer ID on the client.
      </p>
    </div>
  );
}

function ResultView({ result }: { result: MccCheckResult }) {
  const account = result.account;

  let body: ReactElement;
  if (result.state === "linked" && account) {
    const status = account.status.toUpperCase();
    let headline: string;
    if (status === "CANCELED") headline = "In our MCC but the account is cancelled";
    else if (status === "CLOSED") headline = "In our MCC but the account is closed";
    else if (status !== "ENABLED") {
      headline = `In our MCC, account status: ${account.status.toLowerCase()}`;
    } else headline = `Linked to our MCC ${mccLabel(result)}`;
    body = (
      <div className="space-y-1 text-sm">
        <p className="font-medium">{headline}</p>
        <p>{account.name}</p>
        {account.level >= 2 && <p>Linked through a sub-manager account</p>}
        {account.manager && <p>This is a manager account, not an ad account</p>}
      </div>
    );
  } else if (result.state === "not_linked") {
    body = (
      <div className="space-y-3 text-sm">
        <p className="font-medium">{`Not in our MCC ${mccLabel(result)}`}</p>
        {result.hint === "own_mcc" ? (
          <p>{"That is our MCC's own id"}</p>
        ) : (
          <LinkSteps mccFormatted={result.mcc_id_formatted} />
        )}
      </div>
    );
  } else {
    body = (
      <p className="text-sm font-medium">
        {result.reason === "not_configured"
          ? "MCC not configured on this env"
          : UNAVAILABLE_MESSAGE}
      </p>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3">
      {body}
      <p className="text-xs text-muted-foreground">
        {`Checked ID ${formatCustomerId(result.checked_customer_id)}`}
      </p>
      {result.mcc_id_formatted && (
        <p className="text-xs text-muted-foreground">
          {`Checked against MCC ${mccLabel(result)}`}
        </p>
      )}
    </div>
  );
}

export function AdsMccCheckCard({ variant }: { variant: AdsMccCheckVariant }) {
  const inputId = useId();
  const [configState, setConfigState] = useState<ConfigState>({ status: "loading" });
  const [value, setValue] = useState("");
  const [inputError, setInputError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<MccCheckResult | null>(null);
  const [requestFailed, setRequestFailed] = useState(false);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    mounted.current = true;
    clientsService
      .adsMccCheckConfig()
      .then((config) => {
        if (mounted.current) setConfigState({ status: "ready", config });
      })
      .catch((err: unknown) => {
        if (!mounted.current) return;
        setConfigState({ status: responseStatus(err) === 503 ? "off" : "error" });
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  if (configState.status === "loading" || configState.status === "off") return null;

  const heading =
    variant === "panel" ? (
      <h3 className="text-sm font-semibold">Is this account in our Google Ads MCC?</h3>
    ) : null;
  const wrap = (children: ReactElement) =>
    variant === "panel" ? (
      <Card className="space-y-3 border-0 p-4 shadow-sm">{children}</Card>
    ) : (
      <div className="space-y-3">{children}</div>
    );

  if (configState.status === "error") {
    return wrap(
      <>
        {heading}
        <p className="text-sm text-muted-foreground">Couldn&apos;t load the Google Ads check.</p>
      </>,
    );
  }

  const { config } = configState;

  const clearAnswer = () => {
    setResult(null);
    setRequestFailed(false);
    setInputError(null);
  };

  const submit = async () => {
    if (inFlight.current) return;
    const typed = value.trim();
    if (!CUSTOMER_ID_PATTERN.test(typed)) {
      setResult(null);
      setRequestFailed(false);
      setInputError(INVALID_MESSAGE);
      return;
    }
    inFlight.current = true;
    setPending(true);
    clearAnswer();
    const sent = typed.replace(/-/g, "");
    // An answer only counts while the id it was sent for is still in the input;
    // the result also shows the checked id, so a mismatch is visible anyway.
    const stillCurrent = () => valueRef.current.trim().replace(/-/g, "") === sent;
    try {
      const answer = await clientsService.checkAdsMcc(sent);
      if (mounted.current && stillCurrent() && answer.checked_customer_id === sent) {
        setResult(answer);
      }
    } catch {
      if (mounted.current && stillCurrent()) setRequestFailed(true);
    } finally {
      inFlight.current = false;
      if (mounted.current) setPending(false);
    }
  };

  return wrap(
    <>
      {heading}
      {variant === "panel" && config.mcc_id_formatted && (
        <p className="text-xs text-muted-foreground">
          {`Our MCC: ${mccLabel(config)}`}
        </p>
      )}
      <form
        className="space-y-2"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <Label htmlFor={inputId} className="text-xs text-muted-foreground">
          Google Ads customer ID
        </Label>
        <div className="flex items-center gap-2">
          <Input
            id={inputId}
            value={value}
            placeholder="123-456-7890"
            autoComplete="off"
            onChange={(event) => {
              setValue(event.target.value);
              clearAnswer();
            }}
          />
          <Button type="submit" size="sm" disabled={pending}>
            Check
          </Button>
        </div>
        {inputError && (
          <p role="alert" className="text-xs text-destructive">
            {inputError}
          </p>
        )}
      </form>
      {result && <ResultView result={result} />}
      {requestFailed && (
        <div className="rounded-md border border-border bg-muted/40 p-3">
          <p className="text-sm font-medium">{UNAVAILABLE_MESSAGE}</p>
        </div>
      )}
    </>,
  );
}
